import { PureComponent, type ReactNode } from "react";
import { Route, type RouteComponentProps, useParams } from "react-router-dom";

import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";

import { track, useTracking } from "utilities/analytics/analytics";

interface IDetailPageLayoutProps {
    /**
     * @deprecated use the `element` prop instead.
     * Pass the element as a JSX element instead of function.
     *
     * *** Before ***
     *
     * const OutboundPaymentShortLinkPage = (reactRouterProps: RouteComponentProps<{ code: string }>) => {
     *   return <OutboundPaymentShortLink {...reactRouterProps} />;
     *};
     * <DetailPageLayout
     *     path={routes.onlinePayments.OutboundPaymentCodeEntry}
     *     component={OutboundPaymentShortLinkPage}
     *     exact
     * />
     *
     * ** After **
     * Do not use the function, reactRouterProps are propagated through automatically.
     * <DetailPageLayout
     *      path={routes.onlinePayments.OutboundPaymentCodeEntry}
     *      element={<OutboundPaymentShortLink />}
     *      exact
     * />
     *
     */
    component?: React.ComponentType<any>;
    element?: ReactNode;
    path: string;
    exact?: boolean;
}

export class DetailPageLayout extends PureComponent<IDetailPageLayoutProps> {
    private origNavHeight: string;
    componentDidMount() {
        this.origNavHeight = document.documentElement.style.getPropertyValue(
            "--main-navigation-height",
        );
        document.documentElement.style.setProperty("--main-navigation-height", "0px");
    }

    componentWillUnmount() {
        document.documentElement.style.setProperty("--main-navigation-height", this.origNavHeight);
    }

    render() {
        // Core BTLink throws without ambient routing; every other layout supplies it, this one
        // did not (ado-289381). Deliberately no `SpaInfoContext.Provider` alongside it: that would
        // flip isSpa false -> true and change how ~110 `useAutoSPARouting` call sites navigate.
        // The routing provider alone matches what the legacy btWrappers/BTLink shim resolved to.
        const { component: Component, element, path, exact } = this.props;

        if (element) {
            return (
                <Route path={path} exact={exact}>
                    <BTLinkSpaRoutingProvider>
                        <WrappedComponent>{element}</WrappedComponent>
                    </BTLinkSpaRoutingProvider>
                </Route>
            );
        } else if (Component) {
            return (
                <Route
                    path={path}
                    exact={exact}
                    render={(matchProps) => {
                        // Define jobId in tracking scope if defined in the route
                        const routed =
                            matchProps.match.params.jobId !== undefined ? (
                                <TrackedComponent
                                    jobId={parseInt(matchProps.match.params.jobId)}
                                    component={Component}
                                    {...matchProps}
                                />
                            ) : (
                                <Component {...matchProps} />
                            );

                        return <BTLinkSpaRoutingProvider>{routed}</BTLinkSpaRoutingProvider>;
                    }}
                />
            );
        }

        throw new Error("You must provide either a component or an element to DetailPageLayout");
    }
}

/** @deprecated */
interface ITrackedComponentProps extends RouteComponentProps {
    jobId: number;
    component: React.ComponentType<any>;
}

/** @deprecated */
const TrackedComponent: React.FunctionComponent<ITrackedComponentProps> = track(
    (props: ITrackedComponentProps) => ({
        jobId: props.jobId,
    }),
)((props) => {
    const { component: Component, jobId, ...otherProps } = props;
    return <Component {...otherProps} />;
});

interface IWrappedComponentProps {
    children?: ReactNode;
}

function WrappedComponent({ children }: IWrappedComponentProps) {
    const { jobId } = useParams<{ jobId: string }>();
    const { Track } = useTracking({
        jobId: jobId ? Number(jobId) : undefined,
    });

    return <Track>{children}</Track>;
}
